// World-space labels pinned over the 3D scene (design system FloatTag): lock and price tags,
// the rocket's name and sample production figures. They are HTML for crisp text, and are only
// repositioned when the camera moves.
import { Matrix4, Vector3, type PerspectiveCamera } from 'three';
import type { LabelAnchor } from '../render/island-scene';
import { formatNumber, h, icon } from './dom';
import { ICONS } from './icons';
import { layoutLabels, type LabelBox } from './label-layout';
import { resourceIcon } from './resource-icon';

const coin = (): HTMLSpanElement => h('span', { class: 'coin', 'aria-hidden': 'true' });

function labelElement(anchor: LabelAnchor): HTMLElement {
  switch (anchor.kind) {
    case 'lock':
      return h('div', { class: 'tag tag--lock', 'aria-label': `Casilla con candado: ${formatNumber(anchor.price)} monedas` }, icon(ICONS.lockSimple), formatNumber(anchor.price), coin());
    case 'buy':
      return h('div', { class: 'tag tag--lock tag--buy', 'aria-label': `Casilla para comprar: ${formatNumber(anchor.price)} monedas` }, icon(ICONS.lockSimple), formatNumber(anchor.price), coin());
    case 'landmark':
      return h('div', { class: 'tag tag--landmark' }, anchor.text);
    case 'rate':
      return h('div', { class: 'tag tag--rate', 'aria-label': `${anchor.name}: ${anchor.value} por minuto` }, resourceIcon(anchor.resource, 26), String(anchor.value));
  }
}

interface LabelItem {
  readonly anchor: Vector3;
  readonly el: HTMLElement;
  /** Production figures make way for price tags and landmarks when they would overlap. */
  readonly movable: boolean;
  width: number;
  height: number;
}

export class LabelLayer {
  private readonly items: LabelItem[];
  private readonly last = new Matrix4();
  private lastWidth = 0;
  private lastHeight = 0;
  private readonly projected = new Vector3();
  private readonly viewProjection = new Matrix4();

  constructor(root: HTMLElement, anchors: readonly LabelAnchor[]) {
    this.items = anchors.map((a) => {
      const el = labelElement(a);
      root.append(el);
      return { anchor: a.position, el, movable: a.kind === 'rate', width: 0, height: 0 };
    });
  }

  /** Forgets measured sizes, for example once the web font has loaded. */
  remeasure(): void {
    for (const item of this.items) item.width = 0;
    this.last.identity();
  }

  /** Repositions the labels if the camera or the viewport changed since the last call. */
  update(camera: PerspectiveCamera, width: number, height: number): void {
    this.viewProjection.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    if (this.viewProjection.equals(this.last) && width === this.lastWidth && height === this.lastHeight) return;
    this.last.copy(this.viewProjection);
    this.lastWidth = width;
    this.lastHeight = height;
    const shown: LabelItem[] = [];
    const boxes: LabelBox[] = [];
    for (const item of this.items) {
      const v = this.projected.copy(item.anchor).applyMatrix4(this.viewProjection);
      const visible = v.z < 1 && Math.abs(v.x) <= 1 && Math.abs(v.y) < 1.2;
      item.el.hidden = !visible;
      if (!visible) continue;
      // Measured once (after the font loads), then reused so moving labels never forces a layout.
      if (item.width === 0) {
        item.width = item.el.offsetWidth;
        item.height = item.el.offsetHeight;
      }
      shown.push(item);
      boxes.push({ x: ((v.x + 1) / 2) * width, y: ((1 - v.y) / 2) * height, width: item.width, height: item.height, movable: item.movable });
    }
    // A label whose anchor is on screen stays fully readable and clear of its neighbours.
    const xs = layoutLabels(boxes, width);
    shown.forEach((item, i) => {
      const x = xs[i] ?? 0;
      const y = boxes[i]?.y ?? 0;
      item.el.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0) translate(-50%, -100%)`;
    });
  }
}
